import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Minimize2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ---- Flame Avatar SVG ---- */
const FlameAvatar = ({ speaking, idle }) => (
    <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="flameGrad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#FF5A00" />
                <stop offset="50%" stopColor="#FF8C00" />
                <stop offset="100%" stopColor="#2A84FF" />
            </linearGradient>
            <linearGradient id="innerGrad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#FFB347" />
                <stop offset="100%" stopColor="#FF5A00" />
            </linearGradient>
            <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>
        {/* Outer flame */}
        <path
            d="M40 8 C40 8, 18 30, 18 48 C18 62, 28 72, 40 72 C52 72, 62 62, 62 48 C62 30, 40 8, 40 8Z"
            fill="url(#flameGrad)" filter="url(#glow)" opacity="0.9"
        >
            <animateTransform attributeName="transform" type="scale" values="1 1;1.03 1.02;1 1;0.98 1.01;1 1" dur={speaking ? "0.4s" : "2s"} repeatCount="indefinite" additive="sum" />
        </path>
        {/* Inner flame */}
        <path
            d="M40 22 C40 22, 27 38, 27 50 C27 58, 33 64, 40 64 C47 64, 53 58, 53 50 C53 38, 40 22, 40 22Z"
            fill="url(#innerGrad)" opacity="0.85"
        >
            <animateTransform attributeName="transform" type="scale" values="1 1;0.97 1.03;1 1;1.02 0.98;1 1" dur={speaking ? "0.3s" : "1.5s"} repeatCount="indefinite" additive="sum" />
        </path>
        {/* Left eye */}
        <ellipse cx="34" cy="46" rx="3.5" ry={idle ? "3.5" : "4"} fill="white">
            <animate attributeName="ry" values="4;0.5;4" dur="4s" repeatCount="indefinite" begin="2s" />
        </ellipse>
        <circle cx="34" cy="46" r="1.8" fill="#0F192C" />
        {/* Right eye */}
        <ellipse cx="46" cy="46" rx="3.5" ry={idle ? "3.5" : "4"} fill="white">
            <animate attributeName="ry" values="4;0.5;4" dur="4s" repeatCount="indefinite" begin="2s" />
        </ellipse>
        <circle cx="46" cy="46" r="1.8" fill="#0F192C" />
        {/* Mouth */}
        {speaking ? (
            <ellipse cx="40" cy="55" rx="5" ry="3" fill="#0F192C" opacity="0.7">
                <animate attributeName="ry" values="3;1.5;3" dur="0.3s" repeatCount="indefinite" />
            </ellipse>
        ) : (
            <path d="M35 54 Q40 59 45 54" stroke="#0F192C" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7" />
        )}
        {/* Energy sparkles */}
        <circle cx="22" cy="35" r="1" fill="#2A84FF" opacity="0.6">
            <animate attributeName="opacity" values="0;0.8;0" dur="2s" repeatCount="indefinite" />
            <animate attributeName="cy" values="35;28;35" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="58" cy="32" r="1" fill="#FF5A00" opacity="0.6">
            <animate attributeName="opacity" values="0;0.8;0" dur="1.8s" repeatCount="indefinite" begin="0.5s" />
            <animate attributeName="cy" values="32;25;32" dur="1.8s" repeatCount="indefinite" begin="0.5s" />
        </circle>
        <circle cx="40" cy="12" r="0.8" fill="#2A84FF" opacity="0.5">
            <animate attributeName="opacity" values="0;0.7;0" dur="1.5s" repeatCount="indefinite" begin="1s" />
            <animate attributeName="cy" values="12;6;12" dur="1.5s" repeatCount="indefinite" begin="1s" />
        </circle>
    </svg>
);

const AiAssistant = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Ciao! Sono l\'assistente di G-Store. Come posso aiutarti? Posso consigliarti offerte, guidarti nel confronto bollette o aiutarti a prenotare una riparazione.' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const [sessionId] = useState(() => 'chat_' + Date.now());
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    const sendMessage = async () => {
        if (!input.trim() || loading) return;
        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setLoading(true);
        setSpeaking(true);
        try {
            const response = await axios.post(`${API}/api/ai/chat`, {
                message: userMsg,
                session_id: sessionId
            });
            setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        } catch {
            setMessages(prev => [...prev, { role: 'assistant', content: 'Mi scuso, si è verificato un errore. Prova a riscrivere il messaggio.' }]);
        } finally {
            setLoading(false);
            setTimeout(() => setSpeaking(false), 500);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            {/* Floating Avatar Button */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-24 right-6 z-50 w-16 h-16 rounded-full bg-surface border-2 border-primary/50 shadow-[0_0_25px_rgba(255,90,0,0.3)] hover:shadow-[0_0_35px_rgba(255,90,0,0.5)] transition-all duration-300 hover:scale-110 flex items-center justify-center"
                    data-testid="ai-assistant-btn"
                >
                    <div className="w-12 h-12">
                        <FlameAvatar speaking={false} idle={true} />
                    </div>
                    {/* Pulse ring */}
                    <span className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" style={{ animationDuration: '3s' }} />
                </button>
            )}

            {/* Chat Window */}
            {open && (
                <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-5rem)] bg-[#0F192C] border border-border rounded-2xl shadow-[0_0_40px_rgba(255,90,0,0.15)] flex flex-col overflow-hidden animate-fade-in" data-testid="ai-chat-window">
                    {/* Header */}
                    <div className="bg-surface px-4 py-3 flex items-center justify-between border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10">
                                <FlameAvatar speaking={speaking} idle={!speaking} />
                            </div>
                            <div>
                                <p className="font-semibold text-sm">Assistente G-Store</p>
                                <p className="text-xs text-success flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
                                    Online
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated text-muted hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {msg.role === 'assistant' && (
                                    <div className="w-7 h-7 mr-2 flex-shrink-0">
                                        <FlameAvatar speaking={false} idle={true} />
                                    </div>
                                )}
                                <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                    msg.role === 'user'
                                        ? 'bg-primary text-white rounded-br-md'
                                        : 'bg-surface-elevated text-white rounded-bl-md'
                                }`}>
                                    {msg.content}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="w-7 h-7 mr-2 flex-shrink-0">
                                    <FlameAvatar speaking={true} idle={false} />
                                </div>
                                <div className="bg-surface-elevated px-4 py-3 rounded-2xl rounded-bl-md">
                                    <div className="flex gap-1">
                                        <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t border-border bg-surface/50">
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Scrivi un messaggio..."
                                className="flex-1 bg-surface-elevated border border-border rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-muted focus:outline-none focus:border-primary/50"
                                disabled={loading}
                                data-testid="ai-chat-input"
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!input.trim() || loading}
                                className="p-2.5 rounded-xl bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                                data-testid="ai-chat-send"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AiAssistant;
